import { useEffect, useState } from 'react';

import { faLink } from '@fortawesome/pro-light-svg-icons';
import { pipe } from 'ramda';
import { useTranslation } from 'react-i18next';

import { PlanUnit, PlanUnitLink } from './types';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import WithInnerLayout from 'components/shared/CRUD/components/InnerPage/WithInnerLayout';
import { DataGridActionClick } from 'components/shared/DataGrid/DataGrid';

import useNavigateAfterCreate from '../../../../shared/CRUD/components/InnerPage/hooks/useNavigateAfterCreate';
import { LinkPlanUnitDrawer } from './components/LinkPlanUnitDrawer';
import { FilterComponent, Filters } from './components/filter/FilterComponent';
import CreatePlanUnit from './components/form/CreatePlanUnit';
import EditPlanUnit from './components/form/edit/EditPlanUnit';

import { useGetColumns } from './hooks/useGetColumns';
import { useSelector } from '../../../../../hooks';
import { useQueryClient } from 'react-query';
import useGetHierarchyValues from './hooks/useGetHierarchyValues';
import { PRIMARY_LIGHT } from '../../../../../constants/colors';
import {
  filterByReferenceDate,
  filterBySearch,
  filterByClusterId,
  filterPoolId,
  filterEmployerId,
  filterByEmpPoolId
} from './components/utils/filters';
import dayjs from 'dayjs';
import {
  HierarchyPlanUnit,
  createTreeHierarchy
} from './components/utils/treeHierarchy';

type FilterStateTypes = {
  referenceDate: string | number | Date | null;
  clusterId: number | null;
  poolId: number | null;
  empPoolId: number | null;
  employerId: number | null;
  searchTerm: string | null;
  noEmployer: boolean | undefined;
  noPool: boolean | undefined;
  noEmployeePool: boolean | undefined;
};

const PlanUnits = ({ isInnerLayoutOpen, setIsInnerLayoutOpen }) => {
  const { t } = useTranslation();
  const { hasClustersOrgGrant, hasPoolsOrgGrant } = useSelector(
    (state) => state.grants
  );
  const queryClient = useQueryClient();
  const columns = useGetColumns();
  const [filterStates, setFilterStates] = useState<FilterStateTypes>({
    referenceDate: null,
    clusterId: null,
    poolId: null,
    empPoolId: null,
    employerId: null,
    searchTerm: '',
    noEmployer: false,
    noPool: false,
    noEmployeePool: false
  });
  const startDate = dayjs(filterStates.referenceDate).isValid()
    ? dayjs(filterStates.referenceDate).toISOString()
    : '';
  const endDate = dayjs(startDate).isValid()
    ? dayjs(startDate).add(1, 'days').toISOString()
    : '';
  const { data: rows, isFetching } = useGetHierarchyValues(startDate, endDate);
  const [linkMode, setLinkMode] = useState<PlanUnitLink>();
  const [selectedRow, setSelectedRow] = useState<PlanUnit>();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [filteredRows, setFilteredRows] = useState<PlanUnit[] | undefined>();
  const [mergedRows, setMergedRows] = useState<
    PlanUnit[] | HierarchyPlanUnit[] | undefined
  >([]);
  const [collapse, setCollapse] = useState(0);
  const [renderKey, setRenderKey] = useState(0); // force component to re-render due to collapse state change
  const [hideDescendantCount, setHideDescendantCount] = useState(false);

  useEffect(() => {
    if (rows && rows.length > 0) {
      setMergedRows(createTreeHierarchy(rows));
    }
  }, [rows]);

  const toggleInnerLayout = () => {
    setIsInnerLayoutOpen(!isInnerLayoutOpen);
  };

  const onViewCLick = (rowData) => {
    setSelectedRow(rowData);
    setIsInnerLayoutOpen(true);
  };

  useNavigateAfterCreate(rows, onViewCLick);

  const handleOpenLinkDrawer: (
    mode: PlanUnitLink
  ) => DataGridActionClick<PlanUnit> = (mode: PlanUnitLink) => (id, row) => {
    setLinkMode(mode);
    setSelectedRow(row);
    setOpenDrawer(true);
  };

  const handleCloseLinkDrawer = () => {
    setOpenDrawer(false);
  };

  if (isInnerLayoutOpen && selectedRow) {
    return (
      <EditPlanUnit
        rowData={selectedRow}
        setIsInnerLayoutOpen={setIsInnerLayoutOpen}
        toggleInnerLayout={toggleInnerLayout}
        rows={mergedRows}
        onSaveChanges={() => {
          queryClient.invalidateQueries(['planUnits']);
        }}
      />
    );
  }

  const handleFilterChange = async (filters: Filters) => {
    if (isFetching) return mergedRows;
    const {
      referenceDate,
      search,
      clusterId,
      poolId,
      empPoolId,
      employerId,
      noEmployer,
      noPool,
      noEmployeePool
    } = filters;

    setFilterStates({
      referenceDate: filters.referenceDate,
      clusterId: filters.clusterId,
      poolId: filters.poolId,
      empPoolId: filters.empPoolId,
      employerId: filters.employerId,
      searchTerm: filters.search,
      noEmployer: filters.noEmployer,
      noPool: filters.noPool,
      noEmployeePool: filters.noEmployeePool
    });

    const filtered = await pipe(
      (data) => filterByReferenceDate(data, referenceDate),
      (data) => filterBySearch(data, search),
      (data) => filterByClusterId(data, clusterId),
      (data) => filterPoolId(data, poolId, noPool),
      (data) => filterEmployerId(data, employerId, noEmployer),
      (data) => filterByEmpPoolId(data, empPoolId, noEmployeePool)
    )(mergedRows);

    setFilteredRows(filtered);

    setCollapse(
      search ||
        clusterId ||
        poolId ||
        employerId ||
        empPoolId ||
        noPool ||
        noEmployer ||
        noEmployeePool
        ? -1
        : 0
    );
    setHideDescendantCount(!!search);
    setRenderKey((prevKey) => prevKey + 1);
  };

  const getRowClassName = (params) => {
    const isHighlighted = Object.keys(filterStates).some((key) => {
      const value = filterStates[key];
      if (!value) return false;
      if (key === 'searchTerm') {
        return (
          params.row.name.toLowerCase().includes(value.toLowerCase()) ||
          params.row.code.toLowerCase().includes(value.toLowerCase())
        );
      }
      return false;
    });
    return isHighlighted ? 'highlighted-row' : '';
  };

  return (
    <>
      <FilterComponent
        rows={mergedRows}
        handleFilterChange={handleFilterChange}
        dependencies={[mergedRows]}
      />
      <TableCRUDLayout
        key={renderKey} // Use unique key to force re-render
        getRowClassName={getRowClassName}
        editable={false}
        loading={isFetching}
        getRowId={(data) => data.brId}
        getTreeDataPath={(row) => row.hierarchy}
        rows={filteredRows}
        sx={{
          '& .MuiDataGrid-row:not(.Mui-selected)': {
            '&.highlighted-row': {
              backgroundColor: PRIMARY_LIGHT // This color is for highlighted rows
            }
          }
        }}
        columns={columns}
        collapse={collapse}
        hideDescendantCount={hideDescendantCount}
        FormComponent={CreatePlanUnit}
        actionsThreshold={2}
        onViewClick={(rowId, rowData) => onViewCLick(rowData)}
        initialState={{
          sorting: {
            sortModel: [{ field: 'code', sort: 'asc' }]
          }
        }}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        customActions={(id) => [
          {
            icon: faLink,
            tooltipTitle: t('linkTo', {
              mode: `${t(PlanUnitLink.EMPLOYER)[0].toUpperCase()}${t(
                PlanUnitLink.EMPLOYER
              )
                .toLowerCase()
                .substring(1)}`
            }),
            onClick: handleOpenLinkDrawer(PlanUnitLink.EMPLOYER)
          },
          ...(hasClustersOrgGrant
            ? [
                {
                  icon: faLink,
                  tooltipTitle: t('linkTo', {
                    mode: t(PlanUnitLink.CLUSTER)
                  }),
                  onClick: handleOpenLinkDrawer(PlanUnitLink.CLUSTER)
                }
              ]
            : []),
          ...(hasPoolsOrgGrant
            ? [
                {
                  icon: faLink,
                  tooltipTitle: t('manage', { mode: t('employeePool') }),
                  onClick: handleOpenLinkDrawer(PlanUnitLink.EMPLOYEE_POOL)
                },
                {
                  icon: faLink,
                  tooltipTitle: t('manage', { mode: t('requestPool') }),
                  onClick: handleOpenLinkDrawer(PlanUnitLink.REQUEST_POOL)
                }
              ]
            : [])
        ]}
      />
      {selectedRow && (
        <LinkPlanUnitDrawer
          open={openDrawer}
          onClose={handleCloseLinkDrawer}
          selectedRow={selectedRow}
          mode={linkMode}
          hierarchy={mergedRows}
        />
      )}
    </>
  );
};

export default WithInnerLayout(PlanUnits);
